import { useMemo, useRef } from 'react';
import DataSelector from '../DataSelector';
import type { AvailableData, TierHero } from '../types';
import { useSvgClientWidth } from '../hooks/useSvgClientWidth';
import { buildTierBandFillPaths } from '../utils/buildTierBandFillPaths';
import { buildTierBoundaryPolylines } from '../utils/buildTierBoundaryPolylines';
import { buildScatterPoints } from '../utils/buildScatterPoints';
import {
  markerRadiusInViewBox,
  SCATTER_HIT_TARGET_RADIUS_PX,
  SCATTER_THUMB_TARGET_RADIUS_PX,
} from '../utils/scatterMarkerRadiusVb';
import { ScatterFigureLegend } from './ScatterFigureLegend';
import { ScatterHeroMarker } from './ScatterHeroMarker';
import { TierCalculationHow } from './TierCalculationHow';
import { TierCalculationIntuition } from './TierCalculationIntuition';
import { TierCalculationWhyBeta } from './TierCalculationWhyBeta';
import '../HeroCard.css';
import './WinRatePickScatter.css';

const VB_W = 420;
const VB_H = 240;
const PLOT_LEFT = 48;
const PLOT_TOP = 16;
const PLOT_W = VB_W - PLOT_LEFT - 10;
const PLOT_H = VB_H - PLOT_TOP - 40;

interface WinRatePickScatterProps {
  tierHeroes: TierHero[];
  beta: number;
  bigrank: number;
  scrapedAt: string | null;
  availableData: AvailableData[];
  onBigrankChange: (bigrank: number) => void;
  onBetaChange: (beta: number) => void;
  selectedLane: string;
  onLaneChange: (lane: string) => void;
}

export function WinRatePickScatter({
  tierHeroes,
  beta,
  bigrank,
  scrapedAt,
  availableData,
  onBigrankChange,
  onBetaChange,
  selectedLane,
  onLaneChange,
}: WinRatePickScatterProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const layout = { plotWidth: PLOT_W, plotHeight: PLOT_H };
  const points = useMemo(() => buildScatterPoints(tierHeroes, layout), [tierHeroes]);
  const svgClientW = useSvgClientWidth(svgRef, points.length);

  const thumbRadiusVb = useMemo(
    () => markerRadiusInViewBox(SCATTER_THUMB_TARGET_RADIUS_PX, svgClientW, VB_W),
    [svgClientW]
  );
  const hitRadiusVb = useMemo(
    () => markerRadiusInViewBox(SCATTER_HIT_TARGET_RADIUS_PX, svgClientW, VB_W),
    [svgClientW]
  );
  const bandFills = useMemo(
    () => buildTierBandFillPaths(tierHeroes, beta, layout),
    [tierHeroes, beta]
  );
  const boundaryPaths = useMemo(
    () => buildTierBoundaryPolylines(tierHeroes, beta, layout),
    [tierHeroes, beta]
  );

  return (
    <section
      id="tier-calculation-detail"
      className="win-rate-pick-scatter"
      aria-label="ティア算出の詳細が気になる方へ"
    >
      <h2 className="win-rate-pick-scatter__title">ティア算出の詳細が気になる方へ</h2>
      <TierCalculationHow />
      <TierCalculationWhyBeta />
      <TierCalculationIntuition />
      <h3 className="win-rate-pick-scatter__chart-heading">β を変えたときの分布（散布図）</h3>
      {availableData.length > 0 && (
        <DataSelector
          variant="embedded"
          bigrank={bigrank}
          beta={beta}
          scrapedAt={scrapedAt}
          availableData={availableData}
          onBigrankChange={onBigrankChange}
          onBetaChange={onBetaChange}
          selectedLane={selectedLane}
          onLaneChange={onLaneChange}
        />
      )}
      {tierHeroes.length === 0 ? (
        <p className="win-rate-pick-scatter__empty-chart">
          この条件に該当するヒーローがいないため、散布図を表示できません。
        </p>
      ) : (
        <figure className="win-rate-pick-scatter__figure">
          <svg
            ref={svgRef}
            className="win-rate-pick-scatter__svg"
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            role="img"
          >
            <text className="win-rate-pick-scatter__axis-y" x={12} y={VB_H / 2} transform={`rotate(-90 12 ${VB_H / 2})`}>
              勝率 (%)
            </text>
            <text className="win-rate-pick-scatter__axis-x" x={VB_W / 2} y={VB_H - 8} textAnchor="middle">
              バンピック率 (%・log)
            </text>
            <g transform={`translate(${PLOT_LEFT},${PLOT_TOP})`}>
              <rect
                className="win-rate-pick-scatter__plot-bg"
                width={PLOT_W}
                height={PLOT_H}
                rx={4}
              />
              <defs>
                <clipPath id="scatter-plot-clip">
                  <rect width={PLOT_W} height={PLOT_H} rx={4} ry={4} />
                </clipPath>
                {points.map((p) => (
                  <clipPath key={p.heroId} id={`scatter-thumb-${p.heroId}`}>
                    <circle cx={p.cx} cy={p.cy} r={thumbRadiusVb} />
                  </clipPath>
                ))}
              </defs>
              <g clipPath="url(#scatter-plot-clip)">
                {[...bandFills].reverse().map((band) => (
                  <path
                    key={`band-${band.tier}`}
                    d={band.d}
                    className={`win-rate-pick-scatter__tier-band tier-${band.tier.toLowerCase()}`}
                  />
                ))}
                {boundaryPaths.map((d, i) => (
                  <path key={`b-${i}`} d={d} className="win-rate-pick-scatter__tier-boundary" />
                ))}
              </g>
              {points.map((p) => (
                <ScatterHeroMarker
                  key={p.heroId}
                  point={p}
                  thumbRadiusVb={thumbRadiusVb}
                  hitRadiusVb={hitRadiusVb}
                />
              ))}
            </g>
          </svg>
          <ScatterFigureLegend />
        </figure>
      )}
    </section>
  );
}
